import { useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2, Layers } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const PhotoUploadDialog = ({
  open, onOpenChange,
  classes, students,
  bulkData, setBulkData,
  uploading, uploadProgress = { done: 0, total: 0 }, onSubmit,
  triggerDisabled,
  defaultPrice = 10.0,
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  // Students filtered to the currently-picked class. Memoized so it isn't
  // recomputed on every keystroke in the price / student_id inputs.
  const filteredStudents = useMemo(
    () => students.filter((s) => s.class_id === bulkData.class_id),
    [students, bulkData.class_id],
  );

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setBulkData({ ...bulkData, files });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="bg-primary hover:bg-primary/90"
          disabled={triggerDisabled}
          data-testid="upload-photo-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {t('admin.uploadPhotos')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin.uploadPhotos')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('admin.managePhotosDesc')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('nav.photos')}</Label>
            <div
              className="border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {bulkData.files.length > 0 ? (
                <div className="text-center">
                  <Layers className="w-10 h-10 text-primary mx-auto mb-2" />
                  <p className="text-sm font-medium">{bulkData.files.length} {t('admin.selectedFiles')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {bulkData.files.map((f) => f.name).slice(0, 3).join(', ')}
                    {bulkData.files.length > 3 ? '...' : ''}
                  </p>
                </div>
              ) : (
                <>
                  <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t('admin.selectPhotos')}</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              data-testid="bulk-file-input"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('admin.class')}</Label>
            <Select
              value={bulkData.class_id}
              onValueChange={(value) => setBulkData({ ...bulkData, class_id: value })}
            >
              <SelectTrigger className="bg-background border-border" data-testid="bulk-class-select">
                <SelectValue placeholder={t('admin.selectClass')} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {classes.map((cls) => (
                  <SelectItem key={cls.class_id} value={cls.class_id}>
                    {cls.name} ({cls.year})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="bulk_is_class_photo"
              data-testid="bulk-class-photo-checkbox"
              checked={bulkData.is_class_photo}
              onCheckedChange={(checked) => setBulkData({
                ...bulkData,
                is_class_photo: checked,
                is_teacher_photo: false,
                student_id: checked ? '' : bulkData.student_id,
              })}
            />
            <Label htmlFor="bulk_is_class_photo" className="cursor-pointer text-sm">
              {t('admin.isClassPhoto')}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="bulk_is_teacher_photo"
              checked={bulkData.is_teacher_photo}
              onCheckedChange={(checked) => setBulkData({
                ...bulkData,
                is_teacher_photo: checked,
                is_class_photo: false,
                student_id: checked ? '' : bulkData.student_id,
                price: checked ? 0 : (bulkData.price === 0 ? defaultPrice : bulkData.price),
              })}
              data-testid="bulk-teacher-photo-checkbox"
            />
            <Label htmlFor="bulk_is_teacher_photo" className="cursor-pointer text-sm">
              {t('admin.isTeacherPhoto')}
            </Label>
          </div>

          {!bulkData.is_class_photo && !bulkData.is_teacher_photo && bulkData.class_id && (
            <div className="space-y-2">
              <Label>{t('admin.assignToStudent')}</Label>
              <Select
                value={bulkData.student_id}
                onValueChange={(value) => setBulkData({ ...bulkData, student_id: value })}
              >
                <SelectTrigger className="bg-background border-border" data-testid="bulk-student-select">
                  <SelectValue placeholder={t('admin.selectStudent')} />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {filteredStudents.map((student) => (
                    <SelectItem key={student.student_id} value={student.student_id}>
                      {student.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {bulkData.is_teacher_photo ? (
            <div
              className="rounded-sm border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground"
              data-testid="teacher-free-notice"
            >
              {t('admin.teacherPriceNotice')}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="bulk_price">{t('admin.price')}</Label>
              <Input
                id="bulk_price"
                type="number"
                step="0.01"
                min="0"
                value={bulkData.price}
                onChange={(e) => setBulkData({ ...bulkData, price: parseFloat(e.target.value) })}
                className="bg-background border-border"
                data-testid="bulk-price-input"
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={uploading || bulkData.files.length === 0 || !bulkData.class_id}
            data-testid="bulk-upload-submit"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadProgress.total > 0 ? (
                  <span data-testid="upload-progress-label">
                    {t('admin.uploading')} {uploadProgress.done} / {uploadProgress.total}
                  </span>
                ) : (
                  t('admin.uploading')
                )}
              </>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />{t('admin.uploadPhotos')} ({bulkData.files.length})</>
            )}
          </Button>
          {uploading && uploadProgress.total > 0 && (
            <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden" data-testid="upload-progress-bar">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%` }}
              />
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
